/**
 * French translations of the service content defined in servicesData.ts.
 * Keyed by service slug; only the human-readable fields are translated.
 * `icon` and `slug` always come from the English source of truth.
 */
export interface ServiceContentFr {
  group: string;
  title: string;
  tagline: string;
  intro: string;
  highlights: { title: string; desc: string }[];
  deliverables: string[];
  useCases: string[];
}

export const SERVICE_CONTENT_FR: Record<string, ServiceContentFr> = {
  "ai-development": {
    group: "IA et apprentissage automatique",
    title: "Développement IA",
    tagline: "Mettez l'IA au travail dans votre entreprise.",
    intro:
      "Nous concevons et livrons une IA qui résout un vrai problème — répondre aux clients, lire des documents, repérer des tendances ou prédire ce qui va se passer. Vous obtenez un logiciel qui fonctionne, pas un projet de laboratoire.",
    highlights: [
      {
        title: "Solutions IA sur mesure",
        desc: "Conçues autour de vos données et de votre flux de travail, pas d'un modèle générique.",
      },
      {
        title: "Lecture intelligente de documents",
        desc: "Transformez factures, formulaires et PDF en informations claires et exploitables.",
      },
      {
        title: "Prédictions et analyses",
        desc: "Anticipez la demande, détectez la fraude ou signalez les risques avant qu'ils ne coûtent cher.",
      },
      {
        title: "Vos données restent les vôtres",
        desc: "Cloud ou sur site — vous choisissez où vivent vos données.",
      },
    ],
    deliverables: [
      "Atelier de découverte et feuille de route IA claire",
      "Modèle ou fonctionnalité IA fonctionnel intégré à votre produit",
      "Tableau de bord pour suivre la précision et les résultats",
      "Formation et transfert pour votre équipe",
    ],
    useCases: [
      "Trier et répondre automatiquement aux messages clients",
      "Lire et numériser la paperasse",
      "Prévoir les ventes, le stock ou la demande",
      "Recommander le bon produit à chaque client",
    ],
  },
  "ai-agents": {
    group: "IA et apprentissage automatique",
    title: "Agents IA",
    tagline: "Des collègues numériques qui terminent la tâche.",
    intro:
      "Les agents ne se contentent pas de répondre — ils agissent. Ils cherchent l'information, passent à l'étape suivante, mettent à jour vos systèmes et continuent jusqu'à ce que le travail soit fait, 24h/24.",
    highlights: [
      {
        title: "Agents de support",
        desc: "Gèrent les questions courantes et transmettent les cas délicats à un humain.",
      },
      {
        title: "Agents commerciaux",
        desc: "Qualifient les prospects, prennent des rendez-vous et relancent automatiquement.",
      },
      {
        title: "Agents de back-office",
        desc: "Mettent à jour les fiches, créent des tickets et relancent les informations manquantes.",
      },
      {
        title: "Connectés à vos outils",
        desc: "Les agents travaillent dans les applications que vous utilisez déjà chaque jour.",
      },
    ],
    deliverables: [
      "Agent conçu autour de vos véritables parcours de conversation",
      "Garde-fous sûrs et règles de transfert à un humain",
      "Base de connaissances créée à partir de vos propres contenus",
      "Rapports sur les conversations et les résultats",
    ],
    useCases: [
      "Support client de première ligne 24h/24 et 7j/7",
      "Qualification et prise de rendez-vous instantanées des prospects",
      "Demandes automatisées sur les commandes et les comptes",
      "Helpdesk interne pour vos propres collaborateurs",
    ],
  },
  "api-development": {
    group: "Logiciels et intégrations",
    title: "Développement d'API",
    tagline: "Connectez les systèmes pour que les données circulent seules.",
    intro:
      "Nous construisons les API sécurisées et les connexions qui permettent à vos applications, partenaires et outils de communiquer entre eux — pour que l'information cesse d'être recopiée à la main.",
    highlights: [
      {
        title: "API REST et GraphQL",
        desc: "Des points d'accès clairs et bien documentés sur lesquels votre équipe peut compter.",
      },
      {
        title: "Connexions tierces",
        desc: "Paiements, messagerie, cartes, comptabilité et bien plus.",
      },
      {
        title: "Sécurisé dès la conception",
        desc: "Clés, rôles et pistes d'audit intégrés dès le premier jour.",
      },
      {
        title: "Rapide et fiable",
        desc: "Conçu pour absorber un vrai trafic sans flancher.",
      },
    ],
    deliverables: [
      "Conception et documentation de l'API",
      "Développement, tests et déploiement",
      "Authentification et contrôle d'accès",
      "Surveillance et support continus",
    ],
    useCases: [
      "Synchroniser les commandes entre votre boutique et la comptabilité",
      "Envoyer les prospects de votre site web vers votre CRM",
      "Partager des données en toute sécurité avec vos partenaires",
      "Alimenter votre application mobile avec un seul backend",
    ],
  },
  "crm-integration": {
    group: "Logiciels et intégrations",
    title: "Déploiement et intégration CRM",
    tagline: "Chaque prospect et client au même endroit.",
    intro:
      "Nous installons, personnalisons et connectons votre CRM pour que rien ne passe à la trappe. Les prospects arrivent dans le bon pipeline, les relances se font à temps et votre équipe dispose d'une vision claire.",
    highlights: [
      {
        title: "Mise en place et migration",
        desc: "Transférez vos contacts existants sans perdre l'historique.",
      },
      {
        title: "Adapté à votre processus",
        desc: "Pipelines, étapes et champs façonnés selon votre façon de vendre.",
      },
      {
        title: "Relances automatisées",
        desc: "Rappels, tâches et messages déclenchés automatiquement.",
      },
      {
        title: "Connecté partout",
        desc: "Site web, formulaires, e-mail, SMS et réseaux sociaux alimentent le tout.",
      },
    ],
    deliverables: [
      "CRM configuré pour votre processus de vente",
      "Migration et nettoyage des données",
      "Flux de relance automatisés",
      "Formation de l'équipe et accompagnement à l'adoption",
    ],
    useCases: [
      "Ne plus jamais perdre un prospect de votre site ou de vos publicités",
      "Offrir à chaque commercial le même pipeline clair",
      "Déclencher automatiquement des relances au bon moment",
      "Voir toute votre activité commerciale sur un seul tableau de bord",
    ],
  },
  "system-integrations": {
    group: "Logiciels et intégrations",
    title: "Intégrations système",
    tagline: "Faites travailler tous vos outils comme un seul.",
    intro:
      "La plupart des entreprises tournent avec une dizaine d'applications déconnectées. Nous les relions pour que les données circulent automatiquement au lieu d'être ressaisies.",
    highlights: [
      {
        title: "Flux de travail de bout en bout",
        desc: "Une action dans un outil déclenche la bonne étape dans un autre.",
      },
      {
        title: "Fini la double saisie",
        desc: "Saisissez l'information une fois ; elle se met à jour partout où il faut.",
      },
      {
        title: "Fonctionne avec ce que vous avez",
        desc: "Nous connectons vos outils actuels avant d'en acheter de nouveaux.",
      },
      {
        title: "Fiable et surveillé",
        desc: "Vous recevez une alerte si une connexion nécessite votre attention.",
      },
    ],
    deliverables: [
      "Cartographie de vos outils et flux de données actuels",
      "Connexions établies entre vos systèmes clés",
      "Gestion des erreurs et notifications",
      "Documentation de la façon dont tout se connecte",
    ],
    useCases: [
      "Demande du site → CRM → relance e-mail",
      "Commande en ligne → facturation → comptabilité",
      "Paiement reçu → client informé",
      "Formulaire rempli → tâche créée pour votre équipe",
    ],
  },
  "cloud-infrastructure": {
    group: "Cloud et infrastructure",
    title: "Cloud, serveurs et infrastructure",
    tagline: "Hébergement fiable, et des experts à la demande.",
    intro:
      "Du déploiement cloud aux serveurs dédiés en passant par des renforts, nous donnons à votre logiciel un foyer solide et la puissance d'infrastructure dont il a besoin pour grandir.",
    highlights: [
      {
        title: "Déploiements cloud",
        desc: "Configurez et faites évoluer vos systèmes sur AWS, Azure ou Google Cloud.",
      },
      {
        title: "Serveurs et matériel",
        desc: "Provisionnez, configurez et sécurisez les machines qui vous font tourner.",
      },
      {
        title: "Recrutez des ressources infra",
        desc: "Faites appel à des ingénieurs DevOps et infrastructure expérimentés.",
      },
      {
        title: "Surveillance et sauvegardes",
        desc: "Sachez à l'instant où quelque chose vacille — et récupérez vite.",
      },
    ],
    deliverables: [
      "Infrastructure conçue pour votre charge de travail",
      "Pipelines de déploiement automatisés",
      "Surveillance, alertes et sauvegardes quotidiennes",
      "Renforcement de la sécurité et optimisation des coûts",
    ],
    useCases: [
      "Migrer votre application vers le cloud en toute sécurité",
      "Ajouter de la capacité pour une période de pointe",
      "Combler un manque temporaire dans votre équipe IT",
      "Garder les systèmes critiques en ligne et sauvegardés",
    ],
  },
  "messaging-services": {
    group: "Communication",
    title: "Services SMS et e-mail",
    tagline: "Touchez vos clients là où ils regardent vraiment.",
    intro:
      "Des SMS et e-mails fiables à fort taux de délivrabilité qui s'intègrent directement à vos flux — rappels, reçus, offres et relances, envoyés automatiquement au bon moment.",
    highlights: [
      {
        title: "Campagnes SMS",
        desc: "Des messages urgents avec suivi de la livraison.",
      },
      {
        title: "E-mail à grande échelle",
        desc: "Newsletters, séquences et e-mails transactionnels.",
      },
      {
        title: "Envoi déclenché par événement",
        desc: "Les messages partent en fonction de vos propres événements métier.",
      },
      {
        title: "Modèles et analyses",
        desc: "Modèles à votre image avec rapports d'ouverture et de clics.",
      },
    ],
    deliverables: [
      "Configuration de l'expéditeur et bonnes pratiques de délivrabilité",
      "Modèles de marque pour les SMS et les e-mails",
      "Séquences automatisées reliées à votre flux de travail",
      "Rapports de livraison, d'ouverture et de clics",
    ],
    useCases: [
      "Rappels de rendez-vous et de paiement",
      "Messages de bienvenue et d'intégration",
      "Promotions auprès de votre liste de clients",
      "Notifications de commande et de livraison",
    ],
  },
  "website-development": {
    group: "Applications et automatisation",
    title: "Développement de sites web",
    tagline: "Des sites rapides et modernes qui convertissent.",
    intro:
      "Sites marketing, pages de destination et applications web conçus pour charger vite, avoir belle allure et transformer les visiteurs en prospects — reliés à votre CRM et à vos analyses dès le premier jour.",
    highlights: [
      {
        title: "Sites marketing et pages de destination",
        desc: "Conçus pour informer et obtenir le clic.",
      },
      {
        title: "Applications web sur mesure",
        desc: "Portails, tableaux de bord et outils clients.",
      },
      {
        title: "Conçus pour être trouvés",
        desc: "Structure adaptée au référencement et performances soignées.",
      },
      {
        title: "Connectés dès le lancement",
        desc: "Formulaires, chat et suivi alimentant vos systèmes.",
      },
    ],
    deliverables: [
      "Conception et réalisation de votre site ou application web",
      "Mobile-first, adapté à chaque appareil",
      "Formulaires et chat connectés à votre CRM",
      "Analyses et support au lancement",
    ],
    useCases: [
      "Une page de destination à forte conversion pour une campagne",
      "Un site marketing modernisé",
      "Un portail client ou un outil de réservation",
      "Un parcours de devis ou de paiement en ligne",
    ],
  },
  "mobile-app-development": {
    group: "Applications et automatisation",
    title: "Développement d'applications mobiles",
    tagline: "Des applications que vos clients et votre équipe adorent utiliser.",
    intro:
      "Des applications iOS et Android à l'expérience native pour vos clients, votre personnel ou vos équipes terrain — avec un seul backend qui garde tout le monde synchronisé.",
    highlights: [
      {
        title: "Applications clients",
        desc: "Réservation, commande, fidélité et accès au compte.",
      },
      {
        title: "Applications personnel et terrain",
        desc: "Interventions, listes de contrôle et mises à jour depuis n'importe où.",
      },
      {
        title: "Un backend, deux plateformes",
        desc: "Développez une fois et touchez iPhone et Android.",
      },
      {
        title: "Adaptées au mode hors ligne",
        desc: "Continuent de fonctionner quand le réseau disparaît.",
      },
    ],
    deliverables: [
      "Conception, réalisation et publication sur les stores",
      "Backend partagé et tableau de bord d'administration",
      "Notifications push et analyses",
      "Mises à jour et support après lancement",
    ],
    useCases: [
      "Une application de réservation pour vos clients",
      "Une application terrain pour les interventions et les photos",
      "Une application de fidélité ou d'adhésion",
      "Une application interne pour les validations en déplacement",
    ],
  },
  "mobile-workflow-automation": {
    group: "Applications et automatisation",
    title: "Automatisation des flux mobiles",
    tagline: "Vos processus, pilotés depuis un téléphone.",
    intro:
      "Transformez les routines quotidiennes — validations, rapports, pointages, mises à jour d'interventions — en flux mobiles simples qui s'exécutent d'eux-mêmes et gardent votre équipe en mouvement.",
    highlights: [
      {
        title: "Validations en déplacement",
        desc: "Les managers valident depuis une notification, pas depuis un bureau.",
      },
      {
        title: "Mises à jour des interventions et tâches",
        desc: "Le personnel met à jour le statut en direct depuis le terrain.",
      },
      {
        title: "Rapports automatiques",
        desc: "Des synthèses quotidiennes créées et envoyées automatiquement.",
      },
      {
        title: "Fonctionne avec vos outils",
        desc: "Alimente votre CRM, vos feuilles de calcul et votre comptabilité.",
      },
    ],
    deliverables: [
      "Flux de travail cartographiés à partir de votre processus actuel",
      "Formulaires mobiles et étapes de validation",
      "Notifications et rapports automatiques",
      "Intégration avec vos systèmes existants",
    ],
    useCases: [
      "Le personnel terrain met à jour les interventions depuis son téléphone",
      "Les managers valident instantanément les dépenses ou les congés",
      "Rapports quotidiens de ventes et de visites envoyés automatiquement",
      "Listes de contrôle remplies et enregistrées sur le terrain",
    ],
  },
};
